import {api} from "../../../lib/api";
import {pronounceSafeSeq} from "../../../lib/sequences";
import {shrinker} from "@cdx/common";
import {isHeroCard} from "../../workflows/workflow-utils";
import {SearchCategory} from "../search-types";
import {Card, CardId} from "../../../cdx-models/Card";
import {Query, QueryContent} from "../../../cdx-models/utils/MakeModel";
import {CardSuggestion} from "../search-utils";

export const referencePattern = new RegExp(`\\$([${pronounceSafeSeq.letters}]{3,5})`);

type CardType = {
  toConstraint: () => Query<Card>;
  query: string;
  label: string;
  searchLabel: (title?: string | null) => string;
};

const typeInfo = {
  isHero: {
    toConstraint: () => ({
      $or: [
        // either has child cards
        {childCards: {op: "notIn", value: [null, "{}"]}} as any as QueryContent<Card>,
        // or has a deck, but none that allows tasks
        {
          $and: [
            {isDoc: [false, null]},
            {"!deck": {allowedCardTypes: {op: "has", value: "task"}}},
            {deck: {allowedCardTypes: {op: "has", value: "hero"}}},
            {deck: {}},
          ],
        },
      ],
    }),
    label: "Is Hero Card",
    searchLabel: () => `Hero`,
    query: "hero",
  },
  isSub: {
    toConstraint: () => ({parentCardId: {op: "neq", value: null}}),
    label: "Is Sub Card",
    searchLabel: () => `Sub`,
    query: "subcard",
  },
  isLocked: {
    toConstraint: () => ({hasBlockingDeps: true}),
    label: "Is locked",
    searchLabel: () => `Locked`,
    query: "locked",
  },
  isNotLocked: {
    toConstraint: () => ({hasBlockingDeps: false}),
    label: "Is not locked",
    searchLabel: () => `Not locked`,
    query: "locked",
  },
  withCoverImag: {
    toConstraint: () => ({coverFileId: {op: "neq", value: null}}),
    label: "With cover image",
    searchLabel: () => `Cover`,
    query: "coverImage",
  },
  isPrivate: {
    toConstraint: () => ({deckId: null}),
    label: "Private Cards",
    searchLabel: () => `Private`,
    query: "private",
  },
  isDoc: {
    toConstraint: () => ({isDoc: true}),
    label: "Doc Cards",
    searchLabel: () => `Doc`,
    query: "doc",
  },
  isBeast: {
    toConstraint: () => ({
      meta: {op: "jsonSearch", field: "sprintLockedIn.level", jsonOp: "gt", value: 0} as any,
    }),
    label: "Beast Card",
    searchLabel: () => `Beast`,
    query: "beast",
  },
} satisfies Record<string, CardType>;

type Key = keyof typeof typeInfo;

const entries = Object.entries(typeInfo) as [Key, CardType & {cardSpecific: false}][];

const cardCategory: SearchCategory<{type: Key}> = {
  key: "card",
  label: "Card",
  getSuggestions({input, root}) {
    return entries
      .filter(([, {query}]) => query.indexOf(input) === 0)
      .map(([key]) => ({type: key}));
  },
  valueToKey({type}) {
    return type;
  },
  valuesToDbQuery(values) {
    return values.reduce((m, {type}) => ({...m, ...typeInfo[type].toConstraint()}), {});
  },
  renderSuggestion({type}) {
    return typeInfo[type].label;
  },
  renderPill({type}) {
    return typeInfo[type].searchLabel();
  },
  savedSearchLabel({type}) {
    return {prio: 2, label: typeInfo[type].searchLabel()};
  },
};

const subCardCategory: SearchCategory<{id: CardId}> = {
  key: "childCards",
  label: "Sub Cards",
  getSuggestions({input, root, viaTrigger}) {
    const match = input.match(referencePattern);
    if (match) {
      const accountSeq = pronounceSafeSeq.seqToInt(match[1]);
      const card = root.account.$meta.find("cards", {accountSeq})[0] || null;
      if (card && isHeroCard(card)) {
        return [{id: card.cardId as CardId, type: "childCards"}];
      }
    } else if (viaTrigger && input) {
      return root.account.$meta
        .find("cards", {
          title: {op: "contains", value: input},
          visibility: "default",
          ...(process.env.REACT_APP_MODE === "open" && {isPublic: true}),
          $order: "-lastUpdatedAt",
          $limit: 10,
          childCards: {op: "notIn", value: [null, "{}"]} as any as QueryContent<Card>,
        })
        .map((c) => ({id: c.cardId as CardId, type: "childCards"}));
    }
  },
  valueToKey({id}) {
    return id;
  },
  valuesToDbQuery(values) {
    if (values.length === 1) return {parentCardId: values[0].id};
    return {$or: values.map(({id}) => ({parentCardId: id}))};
  },
  renderSuggestion: (value) => <CardSuggestion id={value.id} />,
  renderPill: (value) => <CardSuggestion id={value.id} withinPill />,
  savedSearchLabel({id}) {
    const card = api.getModel({modelName: "card", id});
    return {prio: 2, label: `sub cards: ${shrinker(card?.title || "", 20)}`};
  },
  trigger: {
    value: "sub:",
    emptyHelp: "Enter title or $id of Hero Card",
  },
};

const cardCategories = [cardCategory, subCardCategory];

export default cardCategories;
